import { IonButton, IonIcon, useIonModal } from '@ionic/react';
import { Portal } from '@mui/material';
import { layersOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';

import ImageLayer from '../image-layer/image-layer';
import { SatelliteMapModal } from '../satellite-map-modal/satellite-map-modal';
import {
  SatelliteColorbar,
  SatellitePlotInfo,
} from '../satellite-map/satellite-map.types';
import styles from './satellite-map-control.module.css';

const root = import.meta.env.VITE_API_ROOT;

export interface SatelliteMapControlProps {
  plotsInBounds?: {
    plots?: {
      features?: Array<{
        id: number;
        properties?: {
          name?: string;
          rasters?: SatellitePlotInfo['rasters'];
          bbox?: { corners_coords?: { coordinates?: number[][] } };
        };
      }>;
    };
    colorbar?: SatelliteColorbar;
  };
  imageryIndex: string;
  setImageryIndex: (value: string) => void;
  checkboxValues: Record<number, boolean>;
  onCheckboxChange?: (id: number, checked: boolean) => void;
  position?: string;
  isLoading?: boolean;
}

function buildInfoPlot(
  plotsInBounds: SatelliteMapControlProps['plotsInBounds'],
  checkboxValues: Record<number, boolean>
): SatellitePlotInfo[] {
  return (
    plotsInBounds?.plots?.features?.map((feature) => ({
      plotId: feature.id,
      plotName: feature.properties?.name,
      rasters: feature.properties?.rasters ?? null,
      checked: checkboxValues[feature.id] ?? false,
      coordinates: feature.properties?.bbox?.corners_coords?.coordinates,
    })) ?? []
  );
}

export function SatelliteMapControl(props: SatelliteMapControlProps) {
  const { t } = useTranslation(['map'], { useSuspense: false });
  const { current: map } = useMap();
  const [allPlots, setAllPlots] = useState(false);
  const [dateIndices, setDateIndices] = useState<number[]>([]);
  const [dateIndicesTouched, setDateIndicesTouched] = useState(false);

  const infoPlot = useMemo(
    () => buildInfoPlot(props.plotsInBounds, props.checkboxValues),
    [props.checkboxValues, props.plotsInBounds]
  );

  const colorbar = props.plotsInBounds?.colorbar;
  const numberOfPlots = infoPlot.length;

  const plotsToRender = useMemo(() => {
    if (allPlots) {
      return infoPlot;
    }
    return infoPlot.filter((plot) => plot.checked);
  }, [allPlots, infoPlot]);

  const handleDateChange = (plotIndex: number, direction: 'prev' | 'next') => {
    setDateIndicesTouched(true);
    setDateIndices((prevIndices) => {
      const newIndices = [...prevIndices];
      const rasters = infoPlot[plotIndex]?.rasters ?? [];

      if (direction === 'prev') {
        newIndices[plotIndex] = Math.max((newIndices[plotIndex] ?? 0) - 1, 0);
      } else {
        newIndices[plotIndex] = Math.min(
          (newIndices[plotIndex] ?? 0) + 1,
          Math.max(rasters.length - 1, 0)
        );
      }

      return newIndices;
    });
  };

  const [presentSatMap, dismissSatMap] = useIonModal(SatelliteMapModal, {
    allPlots,
    colorbar,
    dateIndices,
    dismiss: () => dismissSatMap(),
    imageryIndex: props.imageryIndex,
    infoPlot,
    isLoading: props.isLoading,
    numberOfPlots,
    onCheckboxChange: props.onCheckboxChange,
    onDateChange: handleDateChange,
    selectedPlot: props.checkboxValues,
    setAllPlots,
    setImageryIndex: props.setImageryIndex,
  });

  useEffect(() => {
    if (dateIndicesTouched) {
      return;
    }

    const newDateIndices = infoPlot.map((plot) =>
      plot.rasters && plot.rasters.length > 0 ? plot.rasters.length - 1 : 0
    );

    setDateIndices((prevIndices) => {
      if (JSON.stringify(prevIndices) !== JSON.stringify(newDateIndices)) {
        return newDateIndices;
      }
      return prevIndices;
    });
  }, [dateIndicesTouched, infoPlot]);

  useEffect(() => {
    if (!props.imageryIndex) {
      setDateIndicesTouched(false);
    }
  }, [props.imageryIndex]);

  useEffect(() => {
    if (allPlots && infoPlot.some((plot) => !plot.checked)) {
      setAllPlots(false);
    }
  }, [allPlots, infoPlot]);

  return (
    <>
      <Portal
        container={
          map
            ?.getContainer()
            .getElementsByClassName(
              `mapboxgl-ctrl-${props.position || 'top-right'}`
            )[0]
        }
      >
        <IonButton
          className={`${styles.button} mapboxgl-ctrl`}
          data-testid="open-sat-map"
          fill="solid"
          size="small"
          onClick={() => {
            presentSatMap({ cssClass: 'page-modal' });
          }}
        >
          <IonIcon aria-hidden icon={layersOutline} slot="start" />
          {t('map:tools')}
        </IonButton>
      </Portal>

      {props.imageryIndex
        ? plotsToRender.map((plot) => {
            const plotIndex = infoPlot.findIndex(
              (item) => item.plotId === plot.plotId
            );
            const raster = plot.rasters?.[dateIndices[plotIndex] ?? 0];

            if (!raster?.url || !plot.coordinates) {
              return null;
            }

            return (
              <ImageLayer
                key={plot.plotId}
                coordinates={plot.coordinates}
                id={plot.plotId.toString()}
                imageUrl={`${root}${raster.url}`}
              />
            );
          })
        : null}
    </>
  );
}

export default SatelliteMapControl;
